import Highcharts from "highcharts/highstock";

const XAXIS_KEY = "time";
const BAR_KEY = "consumed";
const LINE_KEY = "optimization";
const BAR_FILL = "#413ea0";
const LINE_STROKE = "#1c9c29";

// Helper: find closest point in a series to given x
const findClosestPoint = (series, targetX) => {
  if (!series || !series.data || !series.data.length) return null;
  let closest = series.data[0];
  let minDiff = Math.abs(closest.x - targetX);
  for (let i = 1; i < series.data.length; i++) {
    const p = series.data[i];
    const diff = Math.abs(p.x - targetX);
    if (diff < minDiff) {
      minDiff = diff;
      closest = p;
    }
  }
  return closest;
};

// Helper: find left index (index of the most recent point with x <= targetX)
const findLeftIndex = (series, targetX) => {
  if (!series || !series.data || !series.data.length) return -1;
  let leftIdx = -1;
  for (let i = 0; i < series.data.length; i++) {
    if (series.data[i].x <= targetX) leftIdx = i;
    else break;
  }
  return leftIdx;
};

// Helper: safely extract raw array [ts, y, metrics] from a point if it exists
const getRawFromPoint = (point, series) => {
  if (!point) return null;
  if (Array.isArray(point.options) && point.options.length >= 3) return point.options;
  if (Array.isArray(point.userOptions) && point.userOptions.length >= 3) return point.userOptions;
  if (series && Array.isArray(series.options?.data) && typeof point.index === "number") {
    const raw = series.options.data[point.index];
    if (Array.isArray(raw) && raw.length >= 3) return raw;
  }
  return null;
};

// Colors for custom metrics
const TOOLTIP_COLORS = {
  evse: "#1E88E5",
  siteProduction: "#8E24AA",
  chargeLevel: "#FFEA00",
};

// Create tooltip formatter function
const createTooltipFormatter = () => {
  return function () {
    const x = this.x;
    const chart = this.points && this.points.length ? this.points[0].series.chart : this.series.chart;
    const header = `<b>${Highcharts.dateFormat("%e %b %Y %H:%M:%S", x)}</b><br/>`;

    let evseConsumption = 0;
    let siteProduction = 0;
    let chargeLevel = 0;

    const setpointSeries = chart.series.find(s => s.name === "Setpoint Power");

    if (setpointSeries && Array.isArray(setpointSeries.data) && setpointSeries.data.length) {
      const firstX = setpointSeries.data[0].x;
      const lastX = setpointSeries.data[setpointSeries.data.length - 1].x;

      if (x < firstX || x > lastX) {
        evseConsumption = 0;
        siteProduction = 0;
        chargeLevel = 0;
      } else {
        const leftIndex = findLeftIndex(setpointSeries, x);
        if (leftIndex >= 0) {
          const leftPoint = setpointSeries.data[leftIndex];
          const raw = getRawFromPoint(leftPoint, setpointSeries);
          const metrics = raw ? raw[2] : null;
          if (metrics?.evseConsumption != null) evseConsumption = metrics.evseConsumption;
          if (metrics?.siteProduction != null) siteProduction = metrics.siteProduction;
          if (metrics?.chargeLevel != null) chargeLevel = metrics.chargeLevel;
        }
      }
    } else {
      evseConsumption = 0;
      siteProduction = 0;
      chargeLevel = 0;
    }

    const metaBlock = `
      <span style="color:${TOOLTIP_COLORS.evse}">\u25CF</span> EVSE Consumption: <b>${evseConsumption} W</b><br/>
      <span style="color:${TOOLTIP_COLORS.siteProduction}">\u25CF</span> Site Production: <b>${siteProduction} W</b><br/>
      <span style="color:${TOOLTIP_COLORS.chargeLevel}">\u25CF</span> Charge Level: <b>${chargeLevel} A</b><br/>
    `;

    const body = chart.series
      .filter(s => !s.options.isInternal && s.visible !== false)
      .map(s => {
        if (s === setpointSeries) {
          let displayValue = "NA";
          if (setpointSeries && Array.isArray(setpointSeries.data) && setpointSeries.data.length) {
            const firstX = setpointSeries.data[0].x;
            const lastX = setpointSeries.data[setpointSeries.data.length - 1].x;
            if (x < firstX || x > lastX) {
              displayValue = "0 W";
            } else {
              const leftIndex = findLeftIndex(setpointSeries, x);
              const leftPoint = leftIndex >= 0 ? setpointSeries.data[leftIndex] : null;
              if (leftPoint && leftPoint.y != null) displayValue = `${leftPoint.y} W`;
              else displayValue = "0 W";
            }
          } else {
            displayValue = "0 W";
          }
          return `<span style="color:${s.color}">\u25CF</span> ${s.name}: <b>${displayValue}</b><br/>`;
        }

        const closest = findClosestPoint(s, x);
        const value = closest && closest.y != null ? `${closest.y} W` : "NA";
        return `<span style="color:${s.color}">\u25CF</span> ${s.name}: <b>${value}</b><br/>`;
      })
      .join("");

    return header + metaBlock + body;
  };
};

// Chart options factory function
const createChartOptions = (data, energyConsumptionData) => ({
  chart: {
    type: "line",
    height: "35%",
    marginRight: 20,
    zoomType: "x",
    panning: {
      enabled: true,
    },
    panKey: "shift",
    resetZoomButton: {
      position: {
        x: -50,
        y: -32,
      },
      theme: {
        padding: 4,
        r: 4,
      },
    },
  },
  title: {
    text: '<span style="color:white">.</span>',
    align: "left",
    style: { color: "#333", fontWeight: "bold", fontSize: "16px" },
  },
  xAxis: {
    type: "datetime",
    ordinal: false,
    crosshair: true,
    title: { text: "Time" },
    labels: { style: { color: "#666" } },
  },
  yAxis: {
    title: { text: "Power (W)" },
    gridLineColor: "#eee",
    gridLineDashStyle: "Dash",
    labels: {
      style: { color: "#666" },
      formatter: function () {
        return `${this.value}`;
      },
    },
    opposite: false,
    plotLines: [{ value: 0, color: "#00c853", width: 2 }],
  },
  tooltip: {
    shared: true,
    borderColor: "#ccc",
    backgroundColor: "#fff",
    useHTML: true,
    formatter: createTooltipFormatter(),
  },
  legend: { align: "center", verticalAlign: "bottom" },
  plotOptions: {
    series: {
      animation: false,
      states: { hover: { enabled: true, brightness: 0.05 } },
    },
    area: { marker: { radius: 2, enabled: false }, lineWidth: 2 },
    line: { marker: { enabled: false }, lineWidth: 2 },
  },
  series: [
    {
      name: "Setpoint Power",
      type: "line",
      data: (data && data.setpointPower) || [],
      color: "#00c853",
      lineWidth: 2,
      marker: { enabled: false },
      step: "left",
    },
    {
      name: "Energy Consumption",
      type: "line",
      data: energyConsumptionData || [],
      color: "#FFA500",
      lineWidth: 1,
      marker: { enabled: false },
    },
  ],
  credits: { enabled: false },
  exporting: {
    enabled: true,
    allowHTML: true,
    buttons: {
      contextButton: {
        enabled: true,
        menuItems: [
          "viewFullscreen",
          "printChart",
          "separator",
          "downloadCSV",
          "downloadXLS",
          "downloadPNG",
          "downloadJPEG",
          "downloadPDF",
          "downloadSVG",
        ],
      },
    },
  },
  time: {
    useUTC: false,
  },
});

const CHART_TITLE = "Schedules and Energy Consumption";

export { XAXIS_KEY, BAR_KEY, LINE_KEY, BAR_FILL, LINE_STROKE, createChartOptions, CHART_TITLE };
